import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from 'remark-breaks';
import { getSavedLocale } from "../utils/locale";
import { ShopProductFrame } from "./ShopProductDecoration";

function getGuildIcon(server, className) {
  const name = server.server_name;
  const cacheBuster = server.reloaded_at
    ? new Date(server.reloaded_at).getTime()
    : Date.now();

  if (server.icon) {
    const extension = server.icon.startsWith("a_") ? "gif" : "png";
    return (
      <img
        src={`https://cdn.discordapp.com/icons/${server.server_id}/${server.icon}.${extension}?t=${cacheBuster}`}
        alt={name || "Server Icon"}
        className={className}
      />
    );
  }

  const safeName = name || "?";
  const initials = safeName.split(" ").map((word) => word[0]).join("").substring(0, 3);

  return (
    <div className={`flex items-center justify-center bg-gray-700 text-white font-bold ${className}`}>
      {initials}
    </div>
  );
}

function timeAgo(dateString, locale = "ko") {
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  const minutes = Math.floor(diff / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (locale === "vi") {
    if (days > 0) return `${days} ngày trước`;
    if (hours > 0) return `${hours} giờ trước`;
    if (minutes > 0) return `${minutes} phút trước`;
    return "vừa xong";
  }

  if (days > 0) return `${days}일 전`;
  if (hours > 0) return `${hours}시간 전`;
  if (minutes > 0) return `${minutes}분 전`;
  return "방금 전";
}

// [수정] onTagClick prop 추가
export default function ServerCard({ server, onOpenDetails, onOpenComments, onTagClick, preview, previewWidth, locale }) {
  const activeLocale = locale || getSavedLocale();
  const isVi = activeLocale === "vi";
  const tx = (ko, vi) => (isVi ? vi : ko);
  const [showButton, setShowButton] = useState(false);
  const descRef = useRef(null);

  useEffect(() => {
    const el = descRef.current;
    if (!el) return;

    setTimeout(() => {
      const fullHeight = el.scrollHeight;
      // 내용이 조금이라도 넘치면 버튼 표시 (여유분 2px 정도 둠)
      const isOverflowing = fullHeight > 162;
      setShowButton(isOverflowing);
    }, 0);

  }, [server.description, descRef, preview]);

  const tags = server.tags
    ? server.tags.split(",").filter((tag) => tag.trim().length > 0)
    : [];

  const bannerUrl = server.banner_image
    ? `https://disparty.com/uploads/${server.banner_image}?t=${server.reloaded_at ? new Date(server.reloaded_at).getTime() : Date.now()}`
    : null;
  const shopProduct = server.shopProduct || server.shop_product || null;

  return (
    <ShopProductFrame
      product={shopProduct}
      className={`bg-[#1e1e1e] rounded-2xl overflow-hidden shadow-lg border border-[#333] relative transition-transform duration-150 ease-out transform-gpu flex flex-col font-${server.font || 'default'}`}
      style={
        preview
          ? { maxWidth: previewWidth || 420, width: "100%", margin: "0 auto" }
          : {}
      }
    >
      <div
        className="h-40 w-full bg-center bg-cover relative"
        style={{
          backgroundImage: bannerUrl ? `url(${bannerUrl})` : "none",
          backgroundColor: bannerUrl ? "transparent" : "#2a2a2a",
        }}
      >
        {!preview && (
          <a
            href={server.invite_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!server.invite_url}
            onClick={(event) => {
              if (!server.invite_url) event.preventDefault();
            }}
            className={`absolute top-3 right-3 z-40 px-4 py-1.5 text-sm font-semibold rounded-md shadow-md text-white transition-colors ${server.invite_url
                ? "bg-[#5865F2] hover:bg-[#4a56d1]"
                : "bg-gray-700 text-gray-300 cursor-not-allowed"
              }`}
          >
            {tx("서버 참가", "Tham gia")}
          </a>
        )}
      </div>

      <div className="p-5 relative flex flex-col flex-1">
        <div className="absolute -top-10 left-6">
          {getGuildIcon(
            server,
            "w-20 h-20 rounded-full border-4 border-[#1e1e1e] object-cover"
          )}
        </div>

        <div className="mt-10 flex-1 flex flex-col">
          <h2 className="text-xl font-bold text-white mb-2 flex items-center">
            <span className="break-all">{server.server_name}</span>
            {server.is_nsfw && (
              <span className="ml-2 flex-shrink-0 px-2.5 py-0.5 bg-red-500/20 border border-red-500 text-red-400 text-xs font-semibold rounded-full">
                18+
              </span>
            )}
          </h2>

          {/* [수정] 태그 클릭 기능 추가 (button으로 변경 및 onClick 연결) */}
          <div className="flex flex-wrap gap-2 mb-3">
            {tags.map((tag, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation(); // 카드 상세보기 모달이 열리는 것 방지
                  if (onTagClick) onTagClick(tag);
                }}
                className="bg-[#121212] border border-[#333] text-[#b5b5b5] text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap hover:border-[#5865F2] hover:text-[#5865F2] transition-colors cursor-pointer"
              >
                {tag}
              </button>
            ))}
          </div>
          
          <div
            ref={descRef}
            className="prose prose-invert max-w-none bg-[#252525] border border-[#333] rounded-lg p-3 text-sm text-gray-300 overflow-hidden"
            style={{
              wordBreak: "break-word",
              fontSize: "0.92rem",
              maxHeight: "160px",
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkBreaks]}
              components={{
                p: ({ node, ...props }) => <p className="my-1 leading-snug" {...props} />,
                a: ({ node, ...props }) => <a className="text-blue-400 hover:underline" {...props} />
              }}
            >
              {server.description || tx("서버 설명이 없습니다.", "Không có mô tả server.")}
            </ReactMarkdown>
          </div>
          
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {server.member_count !== undefined && !preview && (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  {server.member_count.toLocaleString()}
                  {tx("명", " người")}
                </div>
              )}
              <p>
                {server.reloaded_at && !preview ? timeAgo(server.reloaded_at, activeLocale) : preview ? tx("미리보기", "Xem trước") : ""}
              </p>
            </div>

            <div className="relative z-40 flex items-center gap-2">
              {/* 댓글 버튼: 미리보기(preview) 아님 && show_comments가 true일 때 표시 */}
              {!preview && server.show_comments && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenComments) onOpenComments(server);
                  }}
                  className="text-sm bg-[#2b2c31] text-gray-300 border border-[#404249] px-3 py-1.5 rounded-md hover:bg-[#36373d] hover:text-white transition flex items-center gap-1"
                  title={tx("익명 댓글", "Bình luận ẩn danh")}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                  <span className="hidden xs:inline">{tx("댓글", "Bình luận")}</span>
                </button>
              )}

              {/* 상세보기 버튼: 내용이 길 때만 표시 */}
              {showButton && (
                <button
                  onClick={() => onOpenDetails && onOpenDetails(server)}
                  className="relative z-40 text-sm bg-[#5865F2] text-white px-3 py-1.5 rounded-md hover:bg-[#4a56d1] transition"
                >
                  {tx("상세보기", "Chi tiết")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </ShopProductFrame>
  );
}
